import React, { useEffect, useId, useRef, useState } from 'react';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Divider, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { ChatTraceExecutionRecord, ChatTraceToolExecution } from '~/services/TripPlannerService';

import ChatTraceJson from './ChatTraceJson';
import { formatTraceDuration } from './chatTraceTiming';

interface Props {
  execution: ChatTraceToolExecution;
  timings?: ChatTraceExecutionRecord[];
}

const PREVIEW_HEIGHT = 280;

export default function ChatTraceToolCall({ execution, timings = [] }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentId = useId();

  useEffect(() => {
    const content = contentRef.current;
    if (!content) {
      return;
    }
    const measure = () => setOverflows(content.scrollHeight > PREVIEW_HEIGHT);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Function Call
        {timings.map((record) => (
          <Typography key={record.id} component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            {formatTraceDuration(record.durationMs)} · {record.status}
          </Typography>
        ))}
      </Typography>
      <Box
        id={contentId}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxHeight: expanded ? undefined : PREVIEW_HEIGHT,
            overflow: 'hidden',
          }}
        >
          <Box ref={contentRef}>
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Arguments
              </Typography>
              <ChatTraceJson functionName={execution.call.name ?? 'Unknown tool'} value={execution.call.arguments} />
            </Box>
            <Divider />
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                Output
              </Typography>
              {execution.result ? (
                <ChatTraceJson value={execution.result.output} />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No output recorded
                </Typography>
              )}
            </Box>
          </Box>
          {overflows && !expanded && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                bottom: 0,
                insetInline: 0,
                height: 56,
                pointerEvents: 'none',
                background: `linear-gradient(transparent, ${alpha(theme.palette.background.paper, 0.98)})`,
              })}
            />
          )}
        </Box>
      </Box>
      {overflows && (
        <Box
          sx={{
            position: expanded ? 'sticky' : 'relative',
            bottom: 0,
            zIndex: 1,
            py: 0.5,
            bgcolor: 'background.paper',
          }}
        >
          <Button
            size="small"
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${execution.call.name ?? 'function call'}`}
            onClick={() => setExpanded((previous) => !previous)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ px: 0, minWidth: 0, color: 'text.secondary', fontSize: 12 }}
          >
            {expanded ? 'Collapse' : 'Expand'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
